import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import Immutable from 'immutable'

import { resolveThemeSettings } from '../../../utils/themes'
import { selectPersistedThemeSettings, selectThemeSettings } from '../../store/utils'

function useResolvedThemeSettings(
  selectSettings: (state: State) => ImmutableMap | undefined,
  computeValues: boolean,
): Immutable.Map<string, any> {
  const themeSettingsSchema = useSelector<State, Immutable.List<Immutable.Map<string, any>>>((state) =>
    state.get('themeSettingsSchema', Immutable.List()),
  )
  const themeSettings = useSelector<State, ImmutableMap>((state) => selectSettings(state) ?? Immutable.Map())
  const defaultThemeSettings = useSelector<State, ImmutableMap>((state) =>
    state.get('defaultThemeSettings', Immutable.Map()),
  )
  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name']))

  return useMemo(
    () =>
      Immutable.fromJS(
        resolveThemeSettings(themeSettingsSchema.toJS(), themeSettings.toJS(), defaultThemeSettings.toJS(), themeName, {
          computeValues,
        }),
      ),
    [themeSettingsSchema, themeSettings, defaultThemeSettings, themeName, computeValues],
  )
}

/**
 * The theme settings the storefront renders, resolved against schema and
 * defaults: the editor's preview while one exists, otherwise the persisted
 * settings.
 */
export function useThemeSettings(computeValues = false): Immutable.Map<string, any> {
  return useResolvedThemeSettings(selectThemeSettings, computeValues)
}

/**
 * The theme settings as saved on the server, resolved against schema and
 * defaults. Unaffected by editor previews, so reverting a preview and the
 * pickers' reset values read from here.
 */
export function usePersistedThemeSettings(): Immutable.Map<string, any> {
  return useResolvedThemeSettings(selectPersistedThemeSettings, false)
}

export function useDefaultThemeSettings(): Immutable.Map<string, any> {
  const themeSettingsSchema = useSelector<State, Immutable.List<Immutable.Map<string, any>>>((state) =>
    state.get('themeSettingsSchema', Immutable.List()),
  )
  const defaultThemeSettings = useSelector<State, ImmutableMap>((state) =>
    state.get('defaultThemeSettings', Immutable.Map()),
  )
  const themeName = useSelector<State, string>((state) => state.getIn(['shop', 'theme', 'name']))

  return useMemo(() => {
    const defaults = defaultThemeSettings.toJS()
    // Pass defaults as both current and fallback so every key resolves to its default value
    return Immutable.fromJS(resolveThemeSettings(themeSettingsSchema.toJS(), defaults, defaults, themeName))
  }, [themeSettingsSchema, defaultThemeSettings, themeName])
}
